import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, forwardRef } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { PlanRefreshService } from '../../../../shared/plan-refresh.service';
import { TabService } from '../../../../shared/tab.service';
import { DetailsComponent } from '../details/details.component';
import { RawMarkdownComponent } from '../raw-markdown/raw-markdown.component';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { marked } from 'marked';
import {
  UpdateArchitecturalPlanPageMetadataRequest,
  GroomArchitecturalPlanPageMetadataRequest,
  UpdateArchitecturalPlanPageMetadataResponse,
  GroomArchitecturalPlanPageMetadataResponse,
  RegionHierarchyTreeNode,
} from '../../../../../generated.commonjs/api_pb';
import { ArchitecturalPlanPage as ProtoArchitecturalPlanPage } from '../../../../../generated.commonjs/plan.reviewer_pb';
import { extractFileIdFromPage } from '../../../../shared/page-path.util';

/**
 * Overview Component with Nested Tabs
 *
 * Displays page metadata and nested tabs for:
 * 1. Explanation - AI-generated page explanation (DetailsComponent)
 * 2. Transcript - Raw page.md content (RawMarkdownComponent)
 * 3. Compliance - Compliance summary (inline HTML)
 *
 * Uses URL fragments for nested tab selection (#explanation, #transcript, #compliance)
 */
@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.css'],
  imports: [
    MatCardModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    // Delayed: TabService <-> Details/RawMarkdown circular import.
    forwardRef(() => DetailsComponent),
    forwardRef(() => RawMarkdownComponent),
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DatePipe,
  ],
})
export class OverviewComponent implements OnInit, OnDestroy {
  private planReviewerService = inject(PlanReviewerService);
  private planRefreshService = inject(PlanRefreshService);
  private tabService = inject(TabService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private fb = inject(FormBuilder);

  activePlanPage: ArchitecturalPlanPage | null = null;
  modelStampDate: Date | null = null;
  loading = false;

  // Form state
  metadataForm!: FormGroup;

  titleChanged: boolean = false;
  sheetIdChanged: boolean = false;
  summaryChanged: boolean = false;
  hasUnsavedChanges: boolean = false;
  savingMetadata: boolean = false;
  isGrooming: boolean = false;

  // Compliance summary (now in nested tab)
  complianceSummaryHtml = '';
  isLoadingComplianceSummary = false;
  hasComplianceSummaryError = false;

  // Nested tab management
  activeNestedTabIndex = 0;
  nestedTabs = [
    { label: 'Explanation', tabId: 'explanation' },
    { label: 'Transcript', tabId: 'transcript' },
    { label: 'Compliance', tabId: 'compliance' },
  ];

  // Region detail (Issue #1228)
  activeRegion: RegionHierarchyTreeNode.AsObject | null = null;
  regionDescriptionHtml = '';
  regionTranscriptHtml = '';

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.metadataForm = this.fb.nonNullable.group({
      title: [''],
      sheetId: [''],
      summary: [''],
    });

    this.metadataForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateChangedFlags());

    // Subscribe to active page changes
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe((page: ArchitecturalPlanPage | null) => {
      this.activePlanPage = page;
      this.modelStampDate = null;
      if (page) {
        const stamp = page.getModelStamp?.();
        const ts = stamp?.getCreatedTime?.();
        if (ts) {
          this.modelStampDate = new Date(ts.getSeconds() * 1000);
        }
        this.fetchComplianceSummary();
        this.loadMetadataIntoForm(page);
      }
    });

    // Subscribe to active region changes (Issue #1228)
    this.tabService.activeRegion$.pipe(takeUntil(this.destroy$)).subscribe(async region => {
      this.activeRegion = region;
      if (region && region.description) {
        this.regionDescriptionHtml = await marked.parse(region.description);
      } else {
        this.regionDescriptionHtml = '';
      }
      if (region && region.transcript) {
        this.regionTranscriptHtml = await marked.parse(region.transcript);
      } else {
        this.regionTranscriptHtml = '';
      }
    });

    // Subscribe to URL fragment changes for tab selection
    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment: string | null) => {
      this.setActiveNestedTabFromFragment(fragment);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets the active nested tab based on URL fragment
   * Defaults to index 0 (Explanation) if no fragment or invalid fragment
   */
  private setActiveNestedTabFromFragment(fragment: string | null): void {
    const tabIndex = this.nestedTabs.findIndex(t => t.tabId === fragment);
    this.activeNestedTabIndex = tabIndex !== -1 ? tabIndex : 0;
  }

  /**
   * Handles nested tab change events
   * Updates URL fragment without full navigation
   */
  onNestedTabChange(event: any): void {
    const tabId = this.nestedTabs[event.index].tabId;

    // Update URL fragment (creates history entry)
    this.router.navigate([], {
      fragment: tabId,
      replaceUrl: false, // Keep in history for back/forward navigation
    });
  }

  /**
   * Fetches compliance summary for the active page
   */
  private async fetchComplianceSummary() {
    if (!this.activePlanPage) return;

    this.isLoadingComplianceSummary = true;
    this.hasComplianceSummaryError = false;
    this.complianceSummaryHtml = '';

    try {
      const complianceReport = await this.planReviewerService.getPageComplianceReport(this.activePlanPage);

      if (complianceReport && complianceReport.getReportSummary) {
        const summaryMd = complianceReport.getReportSummary();
        if (summaryMd && summaryMd.trim().length > 0) {
          this.complianceSummaryHtml = await marked.parse(summaryMd);
        } else {
          this.complianceSummaryHtml = '<p style="color: #888;">No compliance summary available for this page.</p>';
        }
      } else {
        this.complianceSummaryHtml = '<p style="color: #888;">No compliance summary available for this page.</p>';
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error fetching compliance summary:', error);
      this.hasComplianceSummaryError = true;
      this.complianceSummaryHtml = '<p style="color: #d32f2f;">Error loading compliance summary.</p>';
    } finally {
      this.isLoadingComplianceSummary = false;
    }
  }

  private updateChangedFlags(): void {
    if (!this.activePlanPage) {
      this.hasUnsavedChanges = false;
      return;
    }

    const formVal = this.metadataForm.value;
    const origTitle = this.activePlanPage.getTitle() || '';
    const origSheetId = this.activePlanPage.getPageId() || '';
    const origSummary = this.activePlanPage.getSummary() || '';

    this.titleChanged = !!(this.metadataForm.get('title')?.dirty && formVal.title !== origTitle);
    this.sheetIdChanged = !!(this.metadataForm.get('sheetId')?.dirty && formVal.sheetId !== origSheetId);
    this.summaryChanged = !!(this.metadataForm.get('summary')?.dirty && formVal.summary !== origSummary);

    this.hasUnsavedChanges = this.titleChanged || this.sheetIdChanged || this.summaryChanged;
  }

  // ========== METADATA FORM (REACTIVE) ==========

  private loadMetadataIntoForm(page: ArchitecturalPlanPage): void {
    const title = page.getTitle() || '';
    const sheetId = page.getPageId() || '';
    const summary = page.getSummary() || '';

    this.metadataForm.reset({
      title,
      sheetId,
      summary,
    });

    this.titleChanged = false;
    this.sheetIdChanged = false;
    this.summaryChanged = false;
    this.hasUnsavedChanges = false;
  }

  discardChanges(): void {
    if (this.activePlanPage) {
      this.loadMetadataIntoForm(this.activePlanPage);
    }
  }

  saveMetadata(): void {
    if (!this.hasUnsavedChanges || !this.activePlanPage) return;

    this.savingMetadata = true;

    const planId = this.activePlanPage.getArchitecturalPlanId();
    const pageNumber = this.activePlanPage.getPageNumber();
    const fileId = extractFileIdFromPage(this.activePlanPage) || '';

    const formVal = this.metadataForm.value;
    const updateMetadata = new ProtoArchitecturalPlanPage();
    if (this.titleChanged) updateMetadata.setTitle(formVal.title);
    if (this.sheetIdChanged) updateMetadata.setPageId(formVal.sheetId);
    if (this.summaryChanged) updateMetadata.setSummary(formVal.summary);

    const request = new UpdateArchitecturalPlanPageMetadataRequest();
    request.setArchitecturalPlanId(planId);
    request.setPageNumber(pageNumber);
    request.setFileId(fileId);
    request.setMetadata(updateMetadata);

    this.planReviewerService
      .updateArchitecturalPlanPageMetadata(request)
      .then((response: UpdateArchitecturalPlanPageMetadataResponse) => {
        this.savingMetadata = false;
        this.snackBar.open('Metadata saved successfully', 'Close', { duration: 3000 });

        // Update local object and active plan page subject
        if (response && response.getMetadata()) {
          const updatedPage = response.getMetadata();
          if (updatedPage) {
            // Just update our local state by reloading into form
            this.activePlanPage = updatedPage;
            const stamp = updatedPage.getModelStamp?.();
            const ts = stamp?.getCreatedTime?.();
            if (ts) {
              this.modelStampDate = new Date(ts.getSeconds() * 1000);
            } else {
              this.modelStampDate = null;
            }
            this.loadMetadataIntoForm(updatedPage);

            // Refresh the table of contents
            this.planRefreshService.refreshPlanPages(planId, [pageNumber]);
          }
          // Optionally, we could dispatch to tabService but it might trigger reload
        }
      })
      .catch((err: Error) => {
        this.savingMetadata = false;
        ClientLoggingService.log('ERROR', 'Error saving page metadata:', err);
        this.snackBar.open('Failed to save metadata', 'Close', { duration: 3000 });
      });
  }

  clearRegion(): void {
    this.tabService.setActiveRegion(null);
  }

  groomMetadata(): void {
    if (!this.activePlanPage || this.isGrooming) return;

    this.isGrooming = true;
    const planId = this.activePlanPage.getArchitecturalPlanId();
    const pageNumber = this.activePlanPage.getPageNumber();
    const fileId = extractFileIdFromPage(this.activePlanPage) || '';

    const request = new GroomArchitecturalPlanPageMetadataRequest();
    request.setArchitecturalPlanId(planId);
    request.setPageNumber(pageNumber);
    request.setFileId(fileId);
    const formVal = this.metadataForm.value;
    request.setTitle(formVal.title);
    request.setPageId(formVal.sheetId);

    this.planReviewerService
      .groomArchitecturalPlanPageMetadata(request)
      .then((response: GroomArchitecturalPlanPageMetadataResponse) => {
        const metadata = response.getMetadata();
        if (metadata) {
          this.metadataForm.patchValue({
            title: metadata.getTitle(),
            sheetId: metadata.getPageId(),
          });
          this.metadataForm.get('title')?.markAsDirty();
          this.metadataForm.get('sheetId')?.markAsDirty();
          this.updateChangedFlags();
          this.snackBar.open('Metadata groomed successfully', 'Close', { duration: 3000 });
        }
      })
      .catch((err: Error) => {
        ClientLoggingService.log('ERROR', 'Error grooming metadata:', err);
        this.snackBar.open('Failed to groom metadata', 'Close', { duration: 3000 });
      })
      .finally(() => {
        this.isGrooming = false;
      });
  }
}
